'use client'

import Image from 'next/image'
import LocalizedClientLink from '@modules/common/components/localized-client-link'
import { HttpTypes } from '@medusajs/types'
import { useRef } from 'react'

interface ModernFeaturedProductsProps {
  products: HttpTypes.StoreProduct[]
  region: HttpTypes.StoreRegion
  title?: string
}

export function ModernFeaturedProducts({ products, title = 'Featured Products' }: ModernFeaturedProductsProps) {
  const scrollRef = useRef<HTMLDivElement>(null)

  if (!products || products.length === 0) return null

  const scroll = (dir: 'left' | 'right') => {
    if (!scrollRef.current) return
    const amount = 320
    scrollRef.current.scrollBy({ left: dir === 'left' ? -amount : amount, behavior: 'smooth' })
  }

  return (
    <section className="py-16 md:py-20 bg-[#FAFAFA]">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between mb-10">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-gray-900">{title}</h2>
            <p className="text-gray-500 text-sm mt-1">Our top picks for you</p>
          </div>
          <div className="hidden md:flex gap-2">
            <button
              onClick={() => scroll('left')}
              className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-colors"
              aria-label="Scroll left"
            >
              ←
            </button>
            <button
              onClick={() => scroll('right')}
              className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-colors"
              aria-label="Scroll right"
            >
              →
            </button>
          </div>
        </div>

        {/* Horizontal scroll */}
        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {products.map((product) => {
            const thumbnail = product.thumbnail || product.images?.[0]?.url
            const price = product.variants?.[0]?.calculated_price

            return (
              <LocalizedClientLink
                key={product.id}
                href={`/products/${product.handle}`}
                className="group flex-shrink-0 w-[260px] md:w-[280px] snap-start"
              >
                <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-gray-100 mb-3">
                  {thumbnail ? (
                    <Image
                      src={thumbnail}
                      alt={product.title || 'Product'}
                      fill
                      className="object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">
                      No image
                    </div>
                  )}
                </div>
                <h3 className="text-sm font-medium text-gray-900 group-hover:underline underline-offset-4 line-clamp-1">
                  {product.title}
                </h3>
                {price && (
                  <p className="text-sm text-gray-600 mt-1">
                    {new Intl.NumberFormat('en-AU', {
                      style: 'currency',
                      currency: price.currency_code?.toUpperCase() || 'AUD',
                    }).format(price.calculated_amount || 0)}
                  </p>
                )}
              </LocalizedClientLink>
            )
          })}
        </div>
      </div>
    </section>
  )
}
